import { validate as isUuid } from "uuid";

import {
  COMMENT_PARAM,
  type CommentThreadLink,
  commentThreadParams,
} from "@hightouch/lib/llm/asset-comments/paths";
import { useAssetCommentThreadsQuery } from "src/graphql";
import { useCopyUrl } from "src/hooks/use-copy-link";
import { useLocation, useSearchParams } from "src/router";

/**
 * The comment the page's link points at, and a way to copy a link to any
 * other. `linkedThreadId` is the thread the link opens: the one holding the
 * comment, or the one it names on a link from before comments had their own.
 * It is undefined while that is being looked up, and null with no link, no
 * such thread, or a failed lookup.
 */
export function useCommentLink() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const copyUrl = useCopyUrl();
  const linkedId = searchParams.get(COMMENT_PARAM);
  // A param that isn't a uuid names nothing, and the query would refuse it.
  const isLinked = linkedId !== null && isUuid(linkedId);
  const linkedThread = useAssetCommentThreadsQuery(
    {
      subject: {
        _or: [
          { id: { _eq: linkedId } },
          { comments: { id: { _eq: linkedId } } },
        ],
      },
    },
    {
      enabled: isLinked,
      select: (data) => data.asset_comment_threads[0]?.id ?? null,
    },
  );
  return {
    highlightedCommentId: isLinked ? linkedId : null,
    linkedThreadId: !isLinked
      ? null
      : linkedThread.isLoading
        ? undefined
        : (linkedThread.data ?? null),
    copyCommentLink: (comment: CommentThreadLink) =>
      void copyUrl(
        // Without the viewer's params, like an open chat or canvas file: the thread opens its own file.
        `${window.location.origin}${location.pathname}?${commentThreadParams(comment)}`,
      ),
  };
}
